// Collapses the dispute-outcome counts (Won / Lost / Accepted / Refunded) into one
// stacked bar + legend, so the resolution *mix* reads at a glance instead of as 4 equal cards.
interface OutcomesBreakdownProps {
  won: number;
  lost: number;
  accepted: number;
  refunded: number;
}

const SEGMENTS = [
  { key: "won", label: "Won", bar: "bg-success-500", dot: "bg-success-500" },
  { key: "lost", label: "Lost", bar: "bg-error-500", dot: "bg-error-500" },
  { key: "accepted", label: "Accepted", bar: "bg-gray-400", dot: "bg-gray-400" },
  { key: "refunded", label: "Refunded", bar: "bg-blue-light-500", dot: "bg-blue-light-500" },
] as const;

export default function OutcomesBreakdown({ won, lost, accepted, refunded }: OutcomesBreakdownProps) {
  const values: Record<string, number> = { won, lost, accepted, refunded };
  const total = won + lost + accepted + refunded;

  return (
    <div className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
      <span className="text-sm text-gray-500 dark:text-gray-400">Resolution Breakdown</span>

      {total === 0 ? (
        <div className="mt-auto pt-3 text-2xl font-bold text-gray-300 dark:text-gray-600">No resolved disputes</div>
      ) : (
        <>
          <div className="mt-4 flex h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
            {SEGMENTS.filter((s) => values[s.key] > 0).map((s) => (
              // minWidth keeps a small-but-nonzero outcome (e.g. a handful of Accepted) visible
              // instead of collapsing to a sub-pixel sliver.
              <div key={s.key} className={s.bar} style={{ width: `${(values[s.key] / total) * 100}%`, minWidth: "3px" }} />
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
            {SEGMENTS.map((s) => (
              <div key={s.key} className="flex items-center gap-2">
                <span className={`size-2.5 rounded-full ${s.dot}`} />
                <span className="text-sm text-gray-600 dark:text-gray-300">{s.label}</span>
                <span className="text-sm font-semibold text-gray-800 dark:text-white/90">
                  {values[s.key].toLocaleString()}
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {Math.round((values[s.key] / total) * 100)}%
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
